import { useCallback, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { type DeviceCta } from "@njbsoft/field-sync";
import { type components } from "@njbsoft/types/field";
import { Skeleton } from "@/components/ui";
import { RequiresConnection } from "@/components/requires-connection";
import { FieldAccessEnded } from "@/components/tester/chrome/field-access-ended";
import { bundleView } from "@/components/tester/claim/facility-drafts";
import { FacilityDevicesView } from "@/components/tester/claim/facility-devices-view";
import {
  NO_SOURCES,
  useFacilityLocalReads,
} from "@/components/tester/claim/use-facility-local-reads";
import { useFacilityDevices } from "@/hooks/use-facility-devices";
import {
  ClaimOfflineError,
  ClaimRaceError,
  useClaimAssignment,
} from "@/hooks/use-claim-assignment";
import { useIsOnline } from "@/hooks/use-is-online";
import { useToast } from "@/hooks/use-toast";

type FieldFacilityDeviceRow = components["schemas"]["FieldFacilityDeviceRow"];
type FieldFacilityDevicesResponse = components["schemas"]["FieldFacilityDevicesResponse"];

interface FacilitySearch {
  /** A replacement draft waiting for the device it replaced. */
  draft?: string;
}

export const Route = createFileRoute("/_company/claim/facility/$facilityId")({
  validateSearch: (search: Record<string, unknown>): FacilitySearch => ({
    draft: typeof search.draft === "string" && search.draft !== "" ? search.draft : undefined,
  }),
  component: TesterFacilityDevicesPage,
});

const LOADING_ROW_COUNT = 5;

function ListLoadingSkeleton() {
  return (
    <div className="space-y-6 p-4 sm:p-8">
      <Skeleton width="240px" height="36px" />
      <div className="space-y-2">
        {Array.from({ length: LOADING_ROW_COUNT }).map((_, index) => (
          <Skeleton key={`device-row-${String(index)}`} height="96px" />
        ))}
      </div>
    </div>
  );
}

/**
 * The facility's installed devices, each with the action the tester is actually
 * authorized to take: open the assignment they already hold, claim the pool row
 * their company was offered, or — on a device they hold no work on — start a
 * standalone change request. Assemblies not on the list are added from the
 * footer. Every request is staged for the utility's review; nothing here writes
 * the record.
 *
 * A claim here does NOT pull a fresh bundle before navigating, faithful to the
 * phone — so a tester can reach the execution screen before the claimed
 * assignment's local row exists and be told to sync. That is the phone's
 * behaviour, not a defect of this screen.
 *
 * Dual source, like My Work: online the facility-devices read serves the screen,
 * its failure included — a connected device never quietly serves a stale copy.
 * Offline, the bundle's copy of the facility serves it when the device holds one
 * (the same rows, as of the last pull); the bundle carries only the facilities
 * with the tester's work, so any other facility shows the connection panel
 * in-page. A row's pending request is joined to this device's own copy of its
 * set — the bundle's listing, or the outbox row of a set not pulled back yet.
 *
 * Every handover writes its draft sidecar BEFORE it navigates, and the draft's
 * id is all the URL carries. With `?draft=`, a replacement draft is waiting for
 * the device it replaced: a row's Replace device completes that draft instead of
 * starting another.
 */
function TesterFacilityDevicesPage() {
  const { facilityId } = Route.useParams();
  const { draft } = Route.useSearch();
  const { t } = useTranslation(["field-work", "change-requests", "common"]);
  const toast = useToast();
  const navigate = useNavigate();
  const isOffline = !useIsOnline();

  const query = useFacilityDevices(facilityId);
  const { refetch } = query;
  const { reads, reread } = useFacilityLocalReads(facilityId);

  const claimMutation = useClaimAssignment();
  const { mutate: claim } = claimMutation;
  const [claimingId, setClaimingId] = useState<string | null>(null);

  const handleClaim = useCallback(
    (assignmentId: string) => {
      setClaimingId(assignmentId);
      claim(
        { assignmentId },
        {
          onSuccess: () => {
            toast.success("field-work:pool.claimed");
            void navigate({ to: "/my-work/$assignmentId", params: { assignmentId } });
          },
          onError: (err) => {
            if (err instanceof ClaimRaceError) {
              toast.warning("field-work:pool.alreadyClaimed");
              // The row's CTA has to flip from claim to execute, and only a
              // refetch of this list moves it.
              void refetch();
            } else if (err instanceof ClaimOfflineError) {
              toast.warning("field-work:pool.offlineNote");
            } else {
              toast.apiError(err instanceof ApiError ? err.code : undefined);
            }
          },
          // The row spinner is this screen's state; the hook's own settle
          // handler invalidates the queue and never touches it.
          onSettled: () => setClaimingId(null),
        },
      );
    },
    [claim, toast, navigate, refetch],
  );

  const handleCta = useCallback(
    (row: FieldFacilityDeviceRow, cta: DeviceCta) => {
      if (cta === "execute" && row.open_assignment_id) {
        void navigate({
          to: "/my-work/$assignmentId",
          params: { assignmentId: row.open_assignment_id },
        });
        return;
      }
      if (cta === "claim" && row.open_assignment_id) {
        handleClaim(row.open_assignment_id);
      }
    },
    [navigate, handleClaim],
  );

  const handleRefresh = useCallback(() => {
    reread();
    void refetch();
  }, [reread, refetch]);

  if (query.error instanceof ApiError && query.error.status === 403) {
    return <FieldAccessEnded />;
  }

  let view: FieldFacilityDevicesResponse;
  if (isOffline) {
    if (reads === null) return <ListLoadingSkeleton />;
    const bundled = bundleView(reads.stored);
    if (bundled === null) {
      return (
        <RequiresConnection
          showTesterCta={false}
          title={t("change-requests:facility.offlineTitle")}
          body={t("change-requests:facility.offlineBody")}
        />
      );
    }
    view = bundled;
  } else {
    if (query.isLoading) return <ListLoadingSkeleton />;
    if (query.isError || query.data === undefined) {
      return (
        <div className="flex min-h-64 items-center justify-center p-4 sm:p-8">
          <p className="text-danger">{t("common:errors.somethingWentWrong")}</p>
        </div>
      );
    }
    view = query.data;
  }

  return (
    <FacilityDevicesView
      view={view}
      sources={reads?.sources ?? NO_SOURCES}
      draft={draft}
      claimingId={claimingId}
      onCta={handleCta}
      onRefresh={handleRefresh}
    />
  );
}
